// src/context/brandVoice/useBrandDraft.js — the unsaved edits of the Brand Voice screen.
//
// The draft is SPARSE: `{ fields: {path: value}, fieldsEn: {path: value}, voice: null|{label, axes},
// voiceEn: null|{label, axes} }`, holding only what the user actually put a value into. The
// ORIGINALS are the reads' own `field.value` / `field.value_en` and, for the voice,
// `voiceModel.voiceOriginal(lang, …)`, so nothing here keeps a second copy of the server's state
// — which is why a draft survives a re-read and still compares against the FRESH stored value.
//
// ⛔⛔ Decision 1 (2026-09-27): `dirty` is computed, every render, from
// `draftModel.dirtyPaths` — the draft DIFFERS from the stored value. Never "touched": the
// PUT stamps every path it receives as the tenant's, so sending a field whose value was
// typed and then put back would lock the AI out of it for nothing.
//
// ⭐ v190: the voice has ONE draft PER LANGUAGE (`voice` = Italian, `voiceEn` = English), each
// counted as one change. With no English file the English original is the EMPTY seed built
// from the Italian axes (`voiceModel.emptyVoiceFrom`), so an untouched English form is not a
// change.
//
// ⛔ `noteReload` / `noteVoiceReload` are NOT part of the normal flow: they exist for the
// "Ricarica" after a 412 (decision 6), where the stored value may have moved under a draft and
// the user must be told their text will replace someone else's. A save's own re-read drops the
// saved paths first, so nothing is left to conflict with.
import { useCallback, useMemo, useRef, useState } from 'react';
import { sameVoice, voiceDraftFromRead } from './fieldValue.js';
import { conflictsAfterReload, dirtyPaths, voiceDirty } from './draftModel.js';
import { voiceOriginal } from './voiceModel.js';

const EMPTY_DRAFT = { fields: {}, fieldsEn: {}, voice: null, voiceEn: null };
const NO_CONFLICTS = new Set();
// ⭐ v190: the draft slot of each voice language.
const voiceSlot = (lang) => (lang === 'en' ? 'voiceEn' : 'voice');

const without = (map, paths) => {
  const next = {};
  Object.keys(map).forEach((key) => { if (!paths.has(key)) next[key] = map[key]; });
  return next;
};

export default function useBrandDraft({ fields, voiceData, voiceEnData }) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [conflicts, setConflicts] = useState(NO_CONFLICTS);
  const [voiceConflicts, setVoiceConflicts] = useState(NO_CONFLICTS);
  // `noteReload` / `noteVoiceReload` are called from a promise chain, so they must read the
  // CURRENT draft rather than the one captured when the reload was started.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const dirty = useMemo(() => dirtyPaths(fields, draft), [fields, draft]);
  const voiceOriginalIt = useMemo(() => voiceOriginal('it', voiceData, voiceEnData), [voiceData, voiceEnData]);
  const voiceOriginalEn = useMemo(() => voiceOriginal('en', voiceData, voiceEnData), [voiceData, voiceEnData]);
  const voiceIsDirty = voiceDirty(voiceOriginalIt, draft.voice);
  const voiceEnIsDirty = voiceDirty(voiceOriginalEn, draft.voiceEn);
  const dirtyCount = dirty.size + (voiceIsDirty ? 1 : 0) + (voiceEnIsDirty ? 1 : 0);

  /** What a language's form shows: its draft, else its original (⭐ always the editor shape). */
  const voiceValue = (lang) => (lang === 'en'
    ? (draft.voiceEn ?? voiceOriginalEn)
    : (draft.voice ?? voiceOriginalIt));

  const setField = useCallback((path, value) => {
    setDraft((prev) => ({ ...prev, fields: { ...prev.fields, [path]: value } }));
  }, []);

  // Bucket-D fields only (`terminology.avoid`, `messaging.taboo_claims`,
  // `style_examples.avoid`): everything else answers 422 BRAND_GUIDELINES_EN_NOT_EDITABLE.
  const setFieldEn = useCallback((path, value) => {
    setDraft((prev) => ({ ...prev, fieldsEn: { ...prev.fieldsEn, [path]: value } }));
  }, []);

  /** A WHOLE new voice draft `{ label, axes }` for one language (the editor owns its merging). */
  const setVoice = useCallback((value, lang = 'it') => {
    setDraft((prev) => ({ ...prev, [voiceSlot(lang)]: value }));
  }, []);

  const discard = useCallback(() => {
    setDraft(EMPTY_DRAFT);
    setConflicts(NO_CONFLICTS);
    setVoiceConflicts(NO_CONFLICTS);
  }, []);

  /**
   * After a SUCCESSFUL save: the saved paths leave the draft (the re-read now carries
   * them) and lose their conflict marker. A part that failed keeps its draft.
   * ⭐ v190: `{ voice, voiceEn }` drop each voice language on its own.
   */
  const dropSaved = useCallback((paths, { voice, voiceEn } = {}) => {
    const drop = new Set(paths || []);
    setDraft((prev) => ({
      fields: without(prev.fields, drop),
      fieldsEn: without(prev.fieldsEn, drop),
      voice: voice ? null : prev.voice,
      voiceEn: voiceEn ? null : prev.voiceEn,
    }));
    setConflicts((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set([...prev].filter((path) => !drop.has(path)));
      return next.size === prev.size ? prev : next;
    });
    if (voice || voiceEn) {
      setVoiceConflicts((prev) => {
        const next = new Set([...prev].filter((lang) => !(lang === 'en' ? voiceEn : voice)));
        return next.size === prev.size ? prev : next;
      });
    }
  }, []);

  const noteReload = useCallback((prevFields, nextFields) => {
    const found = conflictsAfterReload(prevFields, nextFields, draftRef.current);
    setConflicts(found.size ? found : NO_CONFLICTS);
    return found;
  }, []);

  /**
   * ⭐ v190: after a voice 412 "Ricarica" — marks `lang` when its STORED voice moved under a
   * draft that still differs from the new one (decision 6's rule, for the voice).
   */
  const noteVoiceReload = useCallback((lang, prevRead, nextRead) => {
    const current = draftRef.current[voiceSlot(lang)];
    const after = voiceDraftFromRead(nextRead);
    const moved = !!current && !sameVoice(voiceDraftFromRead(prevRead), after) && !sameVoice(current, after);
    setVoiceConflicts((prev) => {
      if (moved === prev.has(lang)) return prev;
      const next = new Set(prev);
      if (moved) next.add(lang); else next.delete(lang);
      return next;
    });
    return moved;
  }, []);

  return {
    draft,
    dirty,
    dirtyCount,
    voiceIsDirty,
    voiceEnIsDirty,
    voiceValue,
    conflicts,
    voiceConflicts,
    setField,
    setFieldEn,
    setVoice,
    discard,
    dropSaved,
    noteReload,
    noteVoiceReload,
  };
}
